/**
 * Server-side logout (SafeAeon v3 EXT-001). Clearing browser storage alone leaves the JWT valid on every API until it
 * expires; the Auth API's `POST api/UserAuth/Logout` ends the session itself, so the token is refused from then on.
 * Auth-UI's /user/logout page calls it too, but only for tokens still in storage when it loads - any app that clears
 * storage first, or has no logout page configured, needs to call it on its own. `revokeServerSession` and
 * `resolveSessionLogoutUrl` (sessionRedirect.ts) wire these helpers to env.json and the browser storages. No new env
 * key is needed: the endpoint comes from `Auth` / `ConnectToken`, the logout page from the portal's LogOutUrl keys.
 *
 * No imports on purpose: CMSViewer's `yarn test` loads this file directly in Node (test/sessionRevoke.test.mjs).
 */
/**
 * Keys a signed-in token is stored under: Auth-UI writes `internaltoken_{ModuleKey}_{TitleKey}` / `externaltoken_...`,
 * older shells the bare prefix, and the staff-shell handoff uses `token`. Same set as Auth-UI's revokeSession.ts.
 */
const TOKEN_KEY = /^(internaltoken|externaltoken)(_.+)?$|^token$/;
const LOGOUT_PATH = '/api/UserAuth/Logout';
/** Logout page keys, most specific first. Each portal falls back to `LogOutUrl` (and the spellings the portal app read). */
const LOGOUT_URL_KEYS = {
    I: ['InternalLogOutUrl', 'StaffLogOutUrl'],
    E: ['ExternalLogout', 'ExternalLogOutUrl'],
    fallback: ['LogOutUrl', 'logoutUrl', 'logouturl'],
};
const text = (value) => (typeof value === 'string' ? value.trim() : '');
/** 'I' or 'E' from an AccessPortal value: plain or JSON-quoted, any case; EMobile and the like count as E. */
const toPortal = (value) => {
    const first = text(value).replace(/^"+|"+$/g, '').charAt(0).toUpperCase();
    return first === 'I' || first === 'E' ? first : null;
};
/**
 * The signed-in portal: the `AccessPortal` Auth-UI stores at login, else the app's env `AccessPortal`, else the only
 * token prefix present (internaltoken* = I, externaltoken* = E). Null when there is no clue or both prefixes exist.
 */
export const resolvePortal = (stores, envAccessPortal) => {
    let hasInternal = false;
    let hasExternal = false;
    for (const getStore of stores) {
        try {
            const store = getStore();
            const stored = toPortal(store.getItem('AccessPortal'));
            if (stored)
                return stored;
            for (let i = 0; i < store.length; i++) {
                const key = store.key(i) || '';
                if (key.startsWith('internaltoken'))
                    hasInternal = true;
                if (key.startsWith('externaltoken'))
                    hasExternal = true;
            }
        }
        catch {
            // Storage blocked (privacy mode): no clue from this store.
        }
    }
    const fromEnv = toPortal(envAccessPortal);
    if (fromEnv)
        return fromEnv;
    if (hasInternal !== hasExternal)
        return hasInternal ? 'I' : 'E';
    return null;
};
/**
 * The logout page (Auth-UI /user/logout?callbackurl=...) for `portal`: I uses `InternalLogOutUrl`, else
 * `StaffLogOutUrl`; E uses `ExternalLogout` (or `ExternalLogOutUrl`); then `LogOutUrl` for either. An unknown portal
 * gets `LogOutUrl` only, never another portal's page. Empty string when nothing is configured.
 */
export const resolveLogoutUrl = (read, portal) => {
    const keys = [...(portal ? LOGOUT_URL_KEYS[portal] : []), ...LOGOUT_URL_KEYS.fallback];
    for (const key of keys) {
        const url = text(read(key));
        if (url)
            return url;
    }
    return '';
};
/** The `Auth` API base + /api/UserAuth/Logout, else the URL next to `ConnectToken` (.../connect/token). */
export const resolveRevokeUrl = (env) => {
    const authBase = text(env.Auth);
    if (authBase)
        return authBase.replace(/\/+$/, '') + LOGOUT_PATH;
    const connectToken = text(env.ConnectToken);
    if (/\/connect\/token\/?$/i.test(connectToken))
        return connectToken.replace(/\/connect\/token\/?$/i, LOGOUT_PATH);
    return null;
};
/** Every distinct token value in the given storages, in order. A storage that is blocked or throws counts as empty. */
export const collectStoredTokens = (stores) => {
    const tokens = [];
    for (const getStore of stores) {
        try {
            // The accessor is called inside the try: reading window.localStorage itself throws when storage is blocked.
            const store = getStore();
            for (let i = 0; i < store.length; i++) {
                const key = store.key(i);
                const value = key && TOKEN_KEY.test(key) ? (store.getItem(key) || '').trim() : '';
                // The staff shell writes setItem(key, null), which stores the text "null".
                if (value && value !== 'null' && value !== 'undefined' && !tokens.includes(value))
                    tokens.push(value);
            }
        }
        catch {
            // Storage blocked (privacy mode): nothing to collect from this store.
        }
    }
    return tokens;
};
/**
 * POSTs each token to the Auth logout endpoint as its Authorization header ("Bearer <jwt>", as stored). With no token
 * one call is still made, so an IWA session cookie (JWTAuthorization, same origin) is ended too.
 *
 * Bounded and quiet: it resolves after at most `timeoutMs`, never rejects, and returns true only when every call was
 * answered 2xx. A call still pending at the timeout is not aborted - `keepalive` lets it finish even after the page has
 * navigated away, and the Auth API completes a revocation the browser stopped waiting for.
 */
export const revokeSessions = async (url, tokens, fetchImpl = fetch, timeoutMs = 2000) => {
    if (!url)
        return false;
    const credentials = tokens.length ? tokens : [null];
    const calls = Promise.all(credentials.map(async (token) => {
        try {
            const response = await fetchImpl(url, {
                method: 'POST',
                headers: token ? { Authorization: token } : {},
                keepalive: true,
            });
            return Boolean(response && response.ok);
        }
        catch {
            return false;
        }
    })).then((results) => results.every(Boolean));
    let timer;
    const timeout = new Promise((resolve) => {
        timer = setTimeout(() => resolve(false), timeoutMs);
    });
    try {
        return await Promise.race([calls, timeout]);
    }
    finally {
        if (timer)
            clearTimeout(timer);
    }
};
